import { useEnum } from "@/api/services/global/enum";
import { InnerBox } from "@/components/elements/InnerBox";
import { StatusTag } from "@/components/elements/StatusTag";
import { EMPTY_STR } from "@/constants/string";
import { TalentInfoItem } from "@/features/talent/components/talent-detail/TalentInfoItem";
import { TTalentForm } from "@/features/talent/types/talentForm";
import { divider, black } from "@/theme/colors";
import { FCC } from "@/types/common";
import { JAPAN_NATIONALITY_ID } from "@/types/enum";
import { convertNumberToZipCode } from "@/utils/input";
import { findLabelByValue } from "@/utils/object";
import { Stack, Box, Typography, Grid, Divider, styled } from "@mui/material";

type TTalentPartnerProps = {
  defaultValues?: Partial<TTalentForm>;
};

export const TalentPartner: FCC<TTalentPartnerProps> = ({ defaultValues }) => {
  const { data: contactMethodOptions } = useEnum("EPartnerContactMethod");
  const isNotJapan = Number(defaultValues?.partner?.nationalityId) !== JAPAN_NATIONALITY_ID;

  return (
    <InnerBox mt={4} p={3} showDivider={false} borderColor={divider.high} bordered={true}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <Box flex={1}>
          <Typography variant="sub16Bold" color={black[30]}>
            パートナー情報
          </Typography>
        </Box>
      </Stack>

      <Stack mt={2}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="cap12" color={black[70]}>
                パートナー名
              </Typography>

              {defaultValues?.partner?.sendingOrganization && <StatusTag status="disabled" title="送り出し機関" variant="bold" />}
            </Box>

            <Box mt={1}>
              <WrappableTypography variant="body14Semi" color="text.primary">
                {defaultValues?.partner?.partnerName ?? EMPTY_STR.TEXT}
              </WrappableTypography>
            </Box>

            <Divider sx={{ mt: 2 }} />
          </Grid>

          <Grid item xs={12}>
            <TalentInfoItem label="国籍" value={defaultValues?.partner?.nationality?.nationalityName} />
          </Grid>

          {!isNotJapan && (
            <Grid item xs={12}>
              <TalentInfoItem
                label="郵便番号"
                value={defaultValues?.partner?.zipCode ? convertNumberToZipCode(defaultValues?.partner?.zipCode) : EMPTY_STR.TEXT}
              />
            </Grid>
          )}

          {!isNotJapan && (
            <Grid item xs={6}>
              <TalentInfoItem label="都道府県" value={defaultValues?.partner?.prefecture} />
            </Grid>
          )}

          {!isNotJapan && (
            <Grid item xs={6}>
              <TalentInfoItem label="市区町村" value={defaultValues?.partner?.city} />
            </Grid>
          )}

          {!isNotJapan && (
            <Grid item xs={12}>
              <TalentInfoItem label="都道府県・市区町村以外の住所" value={defaultValues?.partner?.address} />
            </Grid>
          )}

          {isNotJapan && (
            <Grid item xs={12}>
              <TalentInfoItem label="所在地" value={defaultValues?.partner?.location} />
            </Grid>
          )}

          <Grid item xs={12}>
            <TalentInfoItem label="担当者" value={defaultValues?.partner?.contactPerson} />
          </Grid>

          <Grid item xs={12}>
            <TalentInfoItem label="連絡方法" value={findLabelByValue(contactMethodOptions, defaultValues?.partner?.contactMethod)} />
          </Grid>

          <Grid item xs={12}>
            <TalentInfoItem label="連絡先" value={defaultValues?.partner?.phoneNumber} />
          </Grid>

          <Grid item xs={12}>
            <TalentInfoItem
              label="メールアドレス"
              value={defaultValues?.partner?.email ?? EMPTY_STR.TEXT}
              isShowIconCopy={defaultValues?.partner?.email ? true : false}
              copyValue={defaultValues?.partner?.email}
            />
          </Grid>

          <Grid item xs={12}>
            <TalentInfoItem label="その他の連絡先" value={defaultValues?.partner?.otherContacts ?? EMPTY_STR.TEXT} />
          </Grid>

          <Grid item xs={12}>
            <TalentInfoItem label="労働局届出状況" value={defaultValues?.partner?.laborBureauStatus ? "済" : EMPTY_STR.TEXT} />
          </Grid>

          <Grid item xs={12}>
            <TalentInfoItem label="契約情報" value={defaultValues?.partner?.contractInfo} />
          </Grid>

          <Grid item xs={12}>
            <TalentInfoItem label="請求情報" value={defaultValues?.partner?.billingInfo} />
          </Grid>

          <Grid item xs={12}>
            <Box
              sx={{
                wordBreak: "break-word",
                overflowWrap: "break-word",
                whiteSpace: "pre-wrap",
              }}
            >
              <TalentInfoItem label="メモ" value={defaultValues?.partner?.memo} type="textArea" isShowDivider={false} />
            </Box>
          </Grid>
        </Grid>
      </Stack>
    </InnerBox>
  );
};

export const WrappableTypography = styled(Typography)`
  word-break: break-word;
  overflow-wrap: break-word;
  white-space: normal;
`;
